import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, LogIn, LogOut, Search, ChevronRight, ArrowLeft, Send, CheckCircle, AlertCircle, Users, ExternalLink, MessageSquare, Calendar, Clock, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

// Import components from separate files
export { Footer } from './Footer';
export { ManageTermsPage } from './ManageTermsPage';
export { PermissionBadge } from './PermissionBadge';
export { PrivacyPolicy } from './PrivacyPolicy';
export { TermsAndConditions } from './TermsAndConditions';
export { TermManagement } from './TermManagement';
export { AllPetitionsPage } from './AllPetitionsPage';

// Login Button Component that uses same auth as header
export const LoginButton = ({ className = "" }) => {
  const [robloxAuth, setRobloxAuth] = useState(null);

  useEffect(() => {
    // Get auth context from window
    if (window.robloxAuthContext) {
      setRobloxAuth(window.robloxAuthContext);
    }
  }, []);

  return (
    <div className="flex justify-center">
      <button
        onClick={() => robloxAuth?.login()}
        disabled={robloxAuth?.loading}
        className={`bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50 ${className}`}
      >
        <LogIn className="w-4 h-4" />
        <span>{robloxAuth?.loading ? 'Connecting...' : 'Login with ROBLOX'}</span>
      </button>
    </div>
  );
};

// Signers List Component
export const SignersList = ({ signers, signatureCount }) => {
  const [showAllSigners, setShowAllSigners] = useState(false);
  
  const validSigners = Array.isArray(signers) ? signers : [];
  const validCount = typeof signatureCount === 'number' ? signatureCount : 0;
  
  if (validSigners.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Signatures ({validCount})</h3>
        <div className="text-center py-8">
          <p className="text-gray-500">Be the first to sign this petition</p>
        </div>
      </div>
    );
  }

  const displayedSigners = showAllSigners ? validSigners : validSigners.slice(0, 10);
  const hasMoreSigners = validSigners.length > 10;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Signatures ({validCount})
      </h3>
      
      <div className="space-y-3">
        {displayedSigners.map((signer, index) => (
          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <img 
              src={signer.avatar || `https://p01--uk-petitions-avatar-proxy--vhbk8ysbhvjz.code.run/avatar/${signer.id}`} 
              alt={signer.displayName || signer.username}
              className="w-10 h-10 rounded-full"
              onError={(e) => {
                e.target.src = '/default-avatar.png';
              }}
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                <a 
                  href={`https://www.roblox.com/users/${signer.id}/profile`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {signer.displayName || signer.username}
                </a>
              </div>
              <div className="text-sm text-gray-500">
                Signed {new Date(signer.signed_at).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit', 
                  year: 'numeric'
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {hasMoreSigners && !showAllSigners && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAllSigners(true)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            + {validSigners.length - 10} more signatures
          </button>
        </div>
      )}
    </div>
  );
};

// Petition status checking - check if petition should be closed due to 3-week rule or term end
export const checkPetitionStatus = async (petition) => {
  // If petition is already closed, don't change its status - preserve closure reason
  if (petition.status === 'closed') {
    return petition.status;
  }

  if (!petition.created) return petition.status;
  
  const createdDate = new Date(petition.created);
  const threeWeeksLater = new Date(createdDate.getTime() + (3 * 7 * 24 * 60 * 60 * 1000));
  const now = new Date();

  // Check if the petition's term has ended
  if (petition.termId) {
    const term = await getTermById(petition.termId);
    if (term && getTermStatus(term) === 'completed' && petition.status === 'open') {
      return 'closed';
    }
  }

  // If 3 weeks have passed and petition hasn't reached minimum threshold (10 signatures)
  // Petitions with 10+ signatures should never be auto-closed
  if (now > threeWeeksLater && petition.signatures < 10 && petition.status === 'open') {
    return 'closed';
  }

  // Check automatic status progression based on signature count and responses
  
  // Priority 1: Handle debate status transitions (regardless of current status)
  if (petition.debateInfo) {
    const debateStatus = petition.debateInfo.status;
    if (debateStatus === "scheduled") {
      return "awaiting_debate_in_parliament";
    } else if (debateStatus === "debated") {
      return "debated_in_parliament";
    } else if (["cancelled", "not_debated"].includes(debateStatus)) {
      return "not_debated_in_parliament";
    }
  }
  
  // Priority 2: If petition has government response, it's no longer awaiting response
  if (petition.governmentResponse && petition.signatures >= 10) {
    return "government_response";
  }
  
  // Priority 3: Check if petition should be awaiting debate (20+ signatures, no debate info)
  if (petition.signatures >= 20 && !petition.debateInfo) {
    return "awaiting_debate_in_parliament";
  }
  
  // Priority 4: Check if petition should be awaiting government response (10+ signatures, no response)
  if (petition.signatures >= 10 && !petition.governmentResponse) {
    return "awaiting_government_response";
  }

  return petition.status;
};

// Update petition status if needed
export const updatePetitionStatus = async (petition) => {
  const newStatus = await checkPetitionStatus(petition);
  if (newStatus !== petition.status) {
    petition.status = newStatus;
    
    // Update localStorage if it's a user petition
    const userPetitions = JSON.parse(localStorage.getItem('user_petitions') || '[]');
    const petitionIndex = userPetitions.findIndex(p => p.id === petition.id);
    if (petitionIndex !== -1) {
      userPetitions[petitionIndex].status = newStatus;
      localStorage.setItem('user_petitions', JSON.stringify(userPetitions));
      window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    }
  }
  return petition;
};

// Government authorization system
const GOVERNMENT_GROUP_ID = 1050282;
export const AUTHORIZED_USER_ID = 1065731316;

// Secure Discord notification function (uses backend endpoint)
const sendDiscordNotification = async (payload) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/discord`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      console.error('Failed to send Discord notification');
    }
  } catch (error) {
    console.error('Error sending Discord notification:', error);
  }
};

export const sendPetitionEventWebhook = async (action, petition, user, content = {}) => {
  try {
    const petitionUrl = `https://ukrblxpetitions.com/petition/${petition.id}`;
    let title, description, color, footerText;

    switch(action) {
      case 'government_response_added':
        title = `📃 Government Response Added`;
        description = `**[${petition.title}](${petitionUrl})** has received a Government Response\n\n**Government Response:**\n${content.response}`;
        color = 0x059669; // Green
        footerText = `Added by ${user.displayName || user.username}, ${content.selectedRole || user.userType}`;
        break;
        
      case 'government_response_edited':
        title = `📝 Government Response Updated`;
        description = `**[${petition.title}](${petitionUrl})** Government Response has been updated\n\n**Previous Response:**\n${content.oldResponse}\n\n**New Response:**\n${content.newResponse}`;
        color = 0xefcc00; // Yellow
        footerText = `Edited by ${user.displayName || user.username}, ${content.selectedRole || user.userType}`;
        break;
        
      case 'government_response_removed':
        title = `🗑️ Government Response Removed`;
        description = `**[${petition.title}](${petitionUrl})** has had its Government Response removed\n\n**Removed Response:**\n${content.removedResponse}`;
        color = 0xdc143c; // Red
        footerText = `Removed by ${user.displayName || user.username}, ${user.userType}`;
        break;
        
      case 'government_response_edited':
        title = `📝 Government Response Updated`;
        description = `**[${petition.title}](${petitionUrl})** has had its Government Response updated\n\n**New Response:**\n${content.newResponse}`;
        color = 0xefcc00; // Yellow
        footerText = `Updated by ${user.displayName || user.username}, ${user.userType}`;
        break;
        
      case 'debate_added':
        title = `🏛️ Parliamentary Debate Scheduled`;
        description = `**[${petition.title}](${petitionUrl})** has been scheduled for parliamentary debate\n\n**Debate Information:**\n**Date:** ${new Date(content.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })}\n**Status:** ${content.status}`;
        color = 0x7c3aed; // Purple
        footerText = `Added by ${user.displayName || user.username}, ${user.userType}`;
        break;
        
      case 'debate_removed':
        title = `🗑️ Parliamentary Debate Removed`;
        description = `**[${petition.title}](${petitionUrl})** has had its debate information removed`;
        color = 0xdc143c; // Red
        footerText = `Removed by ${user.displayName || user.username}, ${user.userType}`;
        break;
        
      case 'debate_edited':
        title = `📝 Parliamentary Debate Updated`;
        description = `**[${petition.title}](${petitionUrl})** has had its debate information updated\n\n**Updated Debate Information:**\n**Date:** ${new Date(content.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })}\n**Status:** ${content.status}`;
        if (content.hansardLink) {
          description += `\n**Hansard Record:** [View Debate Record](${content.hansardLink})`;
        }
        if (content.notDebatedReason) {
          description += `\n**Reason Not Debated:** ${content.notDebatedReason}`;
        }
        color = 0x7c3aed; // Purple
        footerText = `Updated by ${user.displayName || user.username}, ${user.userType}`;
        break;
        
      case 'petition_deleted':
        title = `🗑️ Petition Deleted`;
        description = `**[${petition.title}](${petitionUrl})** has been deleted\n\n**Reason:** ${content.reason || 'Administrative action'}`;
        color = 0xff0000; // Red
        footerText = `Deleted by ${user.displayName || user.username}, ${user.userType}`;
        break;
        
      default:
        return;
    }

    const embed = {
      title,
      description,
      color,
      footer: {
        text: footerText,
        icon_url: getUserAvatar(user.id)
      },
      timestamp: new Date().toISOString()
    };

    await sendDiscordNotification({
      embeds: [embed]
    });

  } catch (error) {
    console.error('Discord petition webhook error:', error);
  }
};

// Helper function to get user avatar URL
export const getUserAvatar = (userId) => {
  return `https://p01--uk-petitions-avatar-proxy--vhbk8ysbhvjz.code.run/avatar/${userId}`;
};

// Update user's profile picture with proxy
const updateUserProfilePicture = (user) => {
  if (!user || !user.id) return user;
  
  return {
    ...user,
    profilePicture: getUserAvatar(user.id),
    profilePictureSmall: getUserAvatar(user.id),
    profilePictureMedium: getUserAvatar(user.id)
  };
};

// Use secure Discord notification function

const WEBHOOK_COLORS = {
  ADDED: 0x059669,     // Green for additions
  EDITED: 0xefcc00,    // Yellow for edits  
  REMOVED: 0xdc143c,   // Red for removals
  MILESTONE: 0x7c3aed  // Purple for milestones
};

// Send Discord webhook notification
const sendDiscordWebhook = async (action, petition, content, user, oldContent = null) => {
  try {
    const petitionUrl = `${window.location.origin}/petition/${petition.id}`;
    let title, description, color;
    
    // Determine action type and set appropriate title, description, and color
    switch(action) {
      case 'threshold_reached':
        title = `🎯 [${petition.title}](${petitionUrl}) has reached ${content.threshold} signatures!`;
        if (content.type === 'government_response') {
          description = `**Milestone Reached!** ✅\nThis petition now qualifies for a Government response.\n\n**Next milestone:** 5 signatures for potential Parliamentary debate consideration.`;
        } else if (content.type === 'parliament_debate') {
          description = `**Major Milestone!** 🏛️\nThis petition now qualifies for consideration of a Parliamentary debate.\n\n**This petition has achieved both milestones:**\n✅ Government response (3+ signatures)\n✅ Parliamentary debate consideration (5+ signatures)`;
        }
        color = WEBHOOK_COLORS.MILESTONE;
        break;
      case 'response_added':
        title = `[${petition.title}](${petitionUrl}) has received a Government Response`;
        description = `**Response Added:**\n${content.text}`;
        color = WEBHOOK_COLORS.ADDED;
        break;
      case 'response_edited':
        title = `[${petition.title}](${petitionUrl}) Government Response has been edited`;
        description = `**Old Response:**\n${oldContent.text}\n\n**New Response:**\n${content.text}`;
        color = WEBHOOK_COLORS.EDITED;
        break;
      case 'response_removed':
        title = `[${petition.title}](${petitionUrl}) Government Response has been removed`;
        description = `**Removed Response:**\n${oldContent.text}`;
        color = WEBHOOK_COLORS.REMOVED;
        break;
      case 'debate_added':
        title = `[${petition.title}](${petitionUrl}) has been scheduled for debate`;
        description = `**Debate Scheduled:**\nDate: ${new Date(content.date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}\nLocation: ${content.location}\nStatus: ${content.status}`;
        color = WEBHOOK_COLORS.ADDED;
        break;
      case 'debate_edited':
        title = `[${petition.title}](${petitionUrl}) debate details have been updated`;
        description = `**Old Details:**\nDate: ${new Date(oldContent.date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}\nLocation: ${oldContent.location}\nStatus: ${oldContent.status}\n\n**New Details:**\nDate: ${new Date(content.date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}\nLocation: ${content.location}\nStatus: ${content.status}`;
        if (content.hansardUrl && !oldContent.hansardUrl) {
          description += `\n\n**Hansard PDF Added:** ${content.hansardUrl}`;
        }
        color = WEBHOOK_COLORS.EDITED;
        break;
      case 'debate_removed':
        title = `[${petition.title}](${petitionUrl}) debate has been cancelled`;
        description = `**Removed Debate:**\nDate: ${new Date(oldContent.date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })}\nLocation: ${oldContent.location}`;
        color = WEBHOOK_COLORS.REMOVED;
        break;
      default:
        return; // Unknown action type
    }

    const embed = {
      title: title,
      description: description,
      color: color,
      footer: {
        text: `${action.includes('added') ? 'Added' : action.includes('edited') ? 'Edited' : 'Removed'} by ${user.username}, ${user.rank === 8 ? 'Prime Minister' : user.rank === 7 ? 'Leader of House of Commons' : 'Cabinet Member'}`,
        icon_url: `https://p01--uk-petitions-avatar-proxy--vhbk8ysbhvjz.code.run/avatar/${user.id}?size=50`
      },
      timestamp: new Date().toISOString()
    };

    await sendDiscordNotification({
      embeds: [embed]
    });

  } catch (error) {
    console.error('Failed to send Discord webhook:', error);
  }
};

// Parliamentary Terms Management
export const PARLIAMENTARY_TERMS = [
  {
    id: 2, 
    name: "Term 87",
    startDate: "2025-05-17", 
    endDate: "2025-07-12",
    status: "current"
  }
];

// Automatically determine term status based on dates
export const getTermStatus = (term) => {
  // Use the status from the API if available, otherwise calculate based on dates
  if (term.status) {
    return term.status;
  }
  
  // Fallback: calculate based on dates (for backward compatibility)
  const currentDate = new Date();
  const startDate = new Date(term.startDate);
  const endDate = new Date(term.endDate);
  
  if (currentDate < startDate) {
    return 'upcoming';
  } else if (currentDate >= startDate && currentDate <= endDate) {
    return 'current';
  } else {
    return 'completed';
  }
};

// Update all terms with correct statuses
export const updateTermStatuses = (terms) => {
  return terms.map(term => ({
    ...term,
    status: getTermStatus(term)
  }));
};

export const getTermById = async (termId) => {
  const terms = await getParliamentaryTerms();
  return terms.find(term => term.id == termId) || null; // Use == to handle string/number comparison
};

// Function to get parliamentary terms from backend API
export const getParliamentaryTerms = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/parliamentary-terms`);
    if (!response.ok) {
      throw new Error('Failed to fetch parliamentary terms');
    }
    const terms = await response.json();
    
    // Update statuses based on current date
    const updatedTerms = updateTermStatuses(terms);
    return updatedTerms;
  } catch (error) {
    console.error('Error fetching parliamentary terms:', error);
    console.warn('Returning fallback term due to error');
    // Return default term as fallback
    return [{
      id: 2,
      name: "Term 87",
      startDate: "2025-05-17",
      endDate: "2025-07-12",
      status: "current"
    }];
  }
};

// Function to create a new parliamentary term via API
export const createParliamentaryTerm = async (termData) => {
  console.log('createParliamentaryTerm called with data:', termData);
  console.log('Backend URL (environment variable):', process.env.REACT_APP_BACKEND_URL);
  
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/parliamentary-terms`;
    console.log('Making POST request to:', url);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(termData)
    });
    
    console.log('Response status:', response.status, 'ok:', response.ok);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response error text:', errorText);
      throw new Error('Failed to create parliamentary term');
    }
    
    const result = await response.json();
    console.log('Created term:', result);
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('termsUpdated'));
    
    return { success: true, data: result };
  } catch (error) {
    console.error('Error creating parliamentary term:', error);
    return { success: false, message: 'Failed to create parliamentary term' };
  }
};

// Function to delete a parliamentary term via API
export const deleteParliamentaryTerm = async (termId) => {
  console.log('deleteParliamentaryTerm called with ID:', termId);
  console.log('Backend URL (environment variable):', process.env.REACT_APP_BACKEND_URL);
  
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/parliamentary-terms/${termId}`;
    console.log('Making DELETE request to:', url);
    
    const response = await fetch(url, {
      method: 'DELETE'
    });
    
    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response error text:', errorText);
      throw new Error('Failed to delete parliamentary term');
    }
    
    const result = await response.json();
    console.log('Response data:', result);
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('termsUpdated'));
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting parliamentary term:', error);
    return { success: false, message: 'Failed to delete parliamentary term' };
  }
};

// Function to update a parliamentary term via API
export const updateParliamentaryTerm = async (termId, updateData) => {
  console.log('updateParliamentaryTerm called with ID:', termId, 'data:', updateData);
  console.log('Backend URL (environment variable):', process.env.REACT_APP_BACKEND_URL);
  
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/parliamentary-terms/${termId}`;
    console.log('Making PATCH request to:', url);
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData)
    });
    
    console.log('Response status:', response.status, 'ok:', response.ok);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response error text:', errorText);
      throw new Error('Failed to update parliamentary term');
    }
    
    const result = await response.json();
    console.log('Updated term:', result);
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('termsUpdated'));
    
    return { success: true, data: result };
  } catch (error) {
    console.error('Error updating parliamentary term:', error);
    return { success: false, message: 'Failed to update parliamentary term' };
  }
};

export const resetParliamentaryTerms = async () => {
  try {
    // Clear localStorage and fetch from database
    localStorage.removeItem('parliamentary_terms');
    localStorage.removeItem('user_petitions');
    
    // Get fresh data from database
    const termsFromDB = await getParliamentaryTerms();
    window.dispatchEvent(new CustomEvent('termsUpdated'));
    
    return termsFromDB;
  } catch (error) {
    console.error('Error resetting parliamentary terms:', error);
    return [];
  }
};

export const saveParliamentaryTerms = (terms) => {
  // Update statuses before saving
  const updatedTerms = updateTermStatuses(terms);
  localStorage.setItem('parliamentary_terms', JSON.stringify(updatedTerms));
  window.dispatchEvent(new CustomEvent('termsUpdated'));
  return updatedTerms;
};

export const getCurrentTerm = async () => {
  const terms = await getParliamentaryTerms();
  // Find current term (automatically determined by dates)
  const currentTerm = terms.find(term => term.status === 'current') || null;
  console.log('getCurrentTerm called, available terms:', terms);
  console.log('current term found:', currentTerm);
  return currentTerm;
};

export const deleteTerm = (termId) => {
  const terms = getParliamentaryTerms();
  const updatedTerms = terms.filter(term => term.id !== termId);
  return saveParliamentaryTerms(updatedTerms);
};

export const getPetitionsByTerm = async (termId) => {
  const allPetitions = await getAllPetitions();
  return allPetitions.filter(petition => petition.termId === termId);
};

export const formatTermDisplay = (term) => {
  if (!term) return "Unknown Term";
  const startDate = new Date(term.startDate).toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
  const endDate = new Date(term.endDate).toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
  return `${term.name} (${startDate} - ${endDate})`;
};

// Government positions for response attribution
export const GOVERNMENT_POSITIONS = [
  'Prime Minister',
  'Deputy Prime Minister',
  'Lord Chancellor and Secretary of State for Justice',
  'Chancellor of the Exchequer', 
  'Secretary of State for Foreign and Commonwealth Affairs',
  'Secretary of State for Justice',
  'Leader of the House of Commons',
  'First Secretary of State',
  'Secretary of State for Northern Ireland',
  'Secretary of State for the Home Department',
  'Leader of the House of Lords',
  'Chief Secretary to the Treasury',
  'Parliamentary Secretary to the Treasury',
  'Attorney General for England and Wales',
  'Minister of State for Emergency Services',
  'Minister of State for Justice',
  'Minister of State for the Commonwealth',
  'Minister of State for Foreign Affairs'
];

// Check if user is government minister
export const isGovernmentMinister = (user) => {
  if (!user) return false;
  
  // System Administrator has full access
  if (user.id === AUTHORIZED_USER_ID.toString()) return true;
  
  // Use government_rank to determine if user is a government minister
  // Rank 6 = Cabinet Minister, Rank 7 = Leader of House, Rank 8 = Prime Minister
  const govRank = user.government_rank || 0;
  return govRank === 6 || govRank === 7 || govRank === 8;
};

// Check if user is a valid British Citizen who can sign/create petitions
export const isBritishCitizen = (user) => {
  if (!user) return false;
  
  // System Administrator has full access
  if (user.id === AUTHORIZED_USER_ID.toString()) return true;
  
  // British Citizens (rank 1+ in UK Group 541807) can sign petitions
  // Backend sets can_sign=true only for these specific ranks
  return user.can_sign === true;
};

// Check if there's a current term running
export const hasCurrentTerm = (terms) => {
  console.log('hasCurrentTerm called with terms:', terms);
  if (!terms || terms.length === 0) {
    console.log('hasCurrentTerm: No terms or empty array');
    return false;
  }
  
  const currentTerms = terms.filter(term => {
    const status = getTermStatus(term);
    console.log(`Term ${term.name}: API status = "${term.status}", calculated status = "${status}"`);
    return status === 'current';
  });
  
  console.log('hasCurrentTerm: found current terms:', currentTerms.length);
  return currentTerms.length > 0;
};

// Get current term (if any)
export const getCurrentTermSafe = (terms) => {
  if (!terms || terms.length === 0) return null;
  return terms.find(term => getTermStatus(term) === 'current') || null;
};
export const canCreatePetitions = (user) => {
  if (!user) return false;
  
  // System Administrator has full access
  if (user.id === AUTHORIZED_USER_ID.toString()) return true;
  
  // ONLY British Citizens can create petitions (no government officials)
  return isBritishCitizen(user);
};


// Petition Signing System
export const getUserSignatures = (userId) => {
  if (!userId) return [];
  const signatures = localStorage.getItem(`user_signatures_${userId}`);
  return signatures ? JSON.parse(signatures) : [];
};

export const saveUserSignatures = (userId, signatures) => {
  if (!userId) return;
  localStorage.setItem(`user_signatures_${userId}`, JSON.stringify(signatures));
};

export const hasUserSignedPetition = (userId, petition) => {
  if (!userId || !petition) return false;
  
  // Check if user is in the petition's signers list
  if (petition.signers && Array.isArray(petition.signers)) {
    return petition.signers.some(signer => String(signer.id) === String(userId));
  }
  
  return false;
};

// Function to sign a petition via API
export const signPetition = async (petitionId, user) => {
  
  if (!user || !petitionId || !isBritishCitizen(user)) {
    return { success: false, error: 'Unauthorized: Only British Citizens can sign petitions' };
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petitionId}/sign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: user.id,
        username: user.username || user.displayName
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return { success: false, error: errorData.detail || 'Failed to sign petition' };
    }
    
    const result = await response.json();
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    window.dispatchEvent(new CustomEvent('signaturesUpdated'));
    
    return { success: true, newCount: result.signatures };
  } catch (error) {
    console.error('Error signing petition:', error);
    return { success: false, error: 'Failed to sign petition. Please try again.' };
  }
};

// Function to unsign a petition via API
export const unsignPetition = async (petitionId, user) => {
  
  if (!user || !petitionId || !isBritishCitizen(user)) {
    return { success: false, error: 'Unauthorized' };
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petitionId}/unsign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: user.id,
        username: user.username || user.displayName
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return { success: false, error: errorData.detail || 'Failed to unsign petition' };
    }
    
    const result = await response.json();
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    window.dispatchEvent(new CustomEvent('signaturesUpdated'));
    
    return { success: true, newCount: result.signatures };
  } catch (error) {
    console.error('Error unsigning petition:', error);
    return { success: false, error: 'Failed to unsign petition. Please try again.' };
  }
};

// Check specific government permissions
export const canAddGovernmentResponse = (user) => {
  if (!user) return false;
  
  // System Administrator (1065731316) has full access
  if (user && (user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316')) {
    return true;
  }
  
  return isGovernmentMinister(user) && (user.government_rank || 0) >= 6; // Cabinet members and above (rank 6-8)
};

export const canRemoveGovernmentResponse = (user) => {
  // System Administrator (1065731316) has full access
  if (user && (user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316')) {
    return true;
  }
  return isGovernmentMinister(user) && (user.government_rank || 0) >= 6; // Cabinet members and above (rank 6-8)
};

export const canEditDebateInfo = (user) => {
  if (!user) return false;
  
  // System Administrator (1065731316) has full access
  if (user && (user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316')) {
    return true;
  }
  
  return isGovernmentMinister(user) && (user.government_rank || 0) >= 7; // Leader of House and Prime Minister (rank 7-8)
};

export const canRemoveDebateInfo = (user) => {
  // System Administrator (1065731316) has full access
  if (user && (user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316')) {
    return true;
  }
  return isGovernmentMinister(user) && (user.government_rank || 0) >= 7; // Leader of House and Prime Minister (rank 7-8)
};

export const canDeletePetitions = (user) => {
  // System Administrator (1065731316) has full access
  if (user && (user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316')) {
    return true;
  }
  return isGovernmentMinister(user) && (user.government_rank || 0) >= 7; // Leader of House and Prime Minister (rank 7-8)
};

export const isPrimeMinister = (user) => {
  // System Administrator (1065731316) has FULL MANAGE TERMS access
  if (user && (user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316')) {
    return true;
  }
  return isGovernmentMinister(user) && (user.government_rank || 0) === 8;
};

// Check if user is system administrator
export const isSystemAdmin = (user) => {
  return user && (user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316');
};

// Check if site is in maintenance mode and user has access
export const hasMaintenanceAccess = (user) => {
  // Check if user is System Administrator
  const isSystemAdmin = (user) => {
    if (!user) return false;
    return user.id === '1065731316' || user.id === 1065731316;
  };
  
  return isSystemAdmin(user); // Only system admin can access during maintenance
};

// Maintenance Mode Management
export const getMaintenanceMode = () => {
  return localStorage.getItem('maintenance_mode') === 'true';
};

export const setMaintenanceMode = (enabled) => {
  localStorage.setItem('maintenance_mode', enabled.toString());
  // Dispatch event to notify all components
  window.dispatchEvent(new CustomEvent('maintenanceModeChanged', { 
    detail: { enabled } 
  }));
};

export const toggleMaintenanceMode = () => {
  const currentMode = getMaintenanceMode();
  const newMode = !currentMode;
  setMaintenanceMode(newMode);
  return newMode;
};
export const MaintenanceMode = () => {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🔧</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Site Under Maintenance</h1>
          <p className="text-gray-600">
            We're currently performing scheduled maintenance to improve your experience.
          </p>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <p className="text-orange-800 text-sm">
            <strong>Estimated time:</strong> This should only take a few minutes.
          </p>
        </div>
        
        <div className="text-sm text-gray-500">
          <p>Please check back shortly.</p>
          <p className="mt-2">UK Parliament Petitions Team</p>
        </div>
      </div>
    </div>
  );
};

export const canManageTerms = (user) => {
  // Only System Administrator (1065731316) has MANAGE TERMS access
  return isSystemAdmin(user);
};

// Function to check and close expired petitions
export const checkExpiredPetitions = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/check-expired`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Error checking expired petitions: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Expired petitions check:', result);
    
    // If any petitions were closed, trigger a refresh
    if (result.closed > 0) {
      window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    }
    
    return result;
  } catch (error) {
    console.error('Error checking expired petitions:', error);
    return null;
  }
};

// Ban Management API Functions
export const getBanStats = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ban-stats`);
    
    if (!response.ok) {
      throw new Error(`Error getting ban stats: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting ban stats:', error);
    return {
      service_available: false,
      total_banned_users: 0,
      error: error.message
    };
  }
};

export const getBannedUsers = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/banned-users`);
    
    if (!response.ok) {
      throw new Error(`Error getting banned users: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting banned users:', error);
    return {
      banned_users: [],
      count: 0,
      error: error.message
    };
  }
};

export const checkUserBan = async (userId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/check-ban/${userId}`);
    
    if (!response.ok) {
      throw new Error(`Error checking user ban: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error checking user ban:', error);
    return {
      user_id: userId,
      is_banned: false,
      message: `Error checking ban status: ${error.message}`
    };
  }
};

export const refreshBanCache = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/refresh-ban-cache`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Error refreshing ban cache: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error refreshing ban cache:', error);
    return {
      error: error.message
    };
  }
};
export const getAllPetitions = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions`);
    if (!response.ok) {
      throw new Error(`Failed to fetch petitions: ${response.status} ${response.statusText}`);
    }
    const petitions = await response.json();
    
    // Check and update petition statuses based on 1-day rule
    const updatedPetitions = await Promise.all(
      petitions.map(petition => updatePetitionStatus({...petition}))
    );
    console.log(`Successfully fetched ${updatedPetitions.length} petitions from backend`);
    return updatedPetitions;
  } catch (error) {
    console.error('Detailed error fetching petitions:', error);
    console.error('Backend URL:', process.env.REACT_APP_BACKEND_URL);
    console.error('Full error details:', {
      message: error.message,
      stack: error.stack,
      url: `${process.env.REACT_APP_BACKEND_URL}/api/petitions`
    });
    return []; // Return empty array if API fails
  }
};

// Function to create a new petition via API
export const createPetition = async (petitionData) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(petitionData)
    });
    
    if (!response.ok) {
      if (response.status === 403) {
        // User is banned from creating petitions
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'You are banned from creating petitions');
      }
      throw new Error('Failed to create petition');
    }
    
    const petition = await response.json();
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    
    return { success: true, petition };
  } catch (error) {
    console.error('Error creating petition:', error);
    return { success: false, message: error.message || 'Failed to create petition' };
  }
};

// Check specific government permissions
export const deletePetition = async (petitionId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petitionId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, message: 'Petition not found' };
      }
      throw new Error('Failed to delete petition');
    }
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    
    return { success: true, message: 'Petition deleted successfully' };
  } catch (error) {
    console.error('Error deleting petition:', error);
    return { success: false, message: 'Failed to delete petition' };
  }
};

// Function to update petition via API
export const updatePetition = async (petitionId, updateData) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petitionId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update petition');
    }
    
    const petition = await response.json();
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    
    return { success: true, petition };
  } catch (error) {
    console.error('Error updating petition:', error);
    return { success: false, message: 'Failed to update petition' };
  }
};

export const updatePetitionStatusAdmin = async (petitionId, status) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petitionId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update petition status');
    }
    
    const result = await response.json();
    
    // Trigger update event
    window.dispatchEvent(new CustomEvent('petitionsUpdated'));
    
    return { success: true, petition: result.petition };
  } catch (error) {
    console.error('Error updating petition status:', error);
    return { success: false, message: error.message || 'Failed to update petition status' };
  }
};

// Function to fix petitions with invalid term IDs
export const fixPetitionsWithInvalidTerms = async () => {
  try {
    const allPetitions = await getAllPetitions();
    const terms = await getParliamentaryTerms();
    const validTermIds = terms.map(t => String(t.id));
    const currentTerm = await getCurrentTerm();
    
    if (!currentTerm) {
      console.error('No current term available for fixing petitions');
      return { success: false, message: 'No current term available' };
    }
    
    const petitionsToFix = allPetitions.filter(petition => 
      !validTermIds.includes(String(petition.termId))
    );
    
    if (petitionsToFix.length === 0) {
      return { success: true, message: 'No petitions need fixing' };
    }
    
    // Update petitions with invalid termIds to current term
    const updatePromises = petitionsToFix.map(petition => 
      updatePetition(petition.id, { termId: String(currentTerm.id) })
    );
    
    const results = await Promise.all(updatePromises);
    const successCount = results.filter(r => r.success).length;
    
    return { 
      success: true, 
      message: `Fixed ${successCount} petition(s) with invalid term IDs` 
    };
  } catch (error) {
    console.error('Error fixing petitions:', error);
    return { success: false, message: 'Failed to fix petitions' };
  }
};

// Custom hook to get parliamentary terms with real-time updates
export const useParliamentaryTerms = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTerms = useCallback(async () => {
    try {
      setError(null);
      const fetchedTerms = await getParliamentaryTerms();
      setTerms(fetchedTerms);
    } catch (err) {
      console.error('Error loading parliamentary terms:', err);
      setError(err.message);
      setTerms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTerms();
    
    // Listen for term updates
    const handleTermsUpdate = () => {
      loadTerms();
    };
    
    window.addEventListener('termsUpdated', handleTermsUpdate);
    
    // Poll for updates every 30 seconds to sync across browsers
    const pollInterval = setInterval(() => {
      loadTerms();
    }, 30000);
    
    return () => {
      window.removeEventListener('termsUpdated', handleTermsUpdate);
      clearInterval(pollInterval);
    };
  }, [loadTerms]);

  return { terms, loading, error, refresh: loadTerms };
};
export const usePetitions = () => {
  const [petitions, setPetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Function to fetch petitions from API
  const fetchPetitions = async () => {
    setLoading(true);
    try {
      const fetchedPetitions = await getAllPetitions();
      setPetitions(fetchedPetitions);
    } catch (error) {
      console.error('Error fetching petitions:', error);
      setPetitions([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchPetitions();
  }, []);
  
  // Listen for custom events when petitions are updated
  useEffect(() => {
    const handlePetitionsUpdate = () => {
      fetchPetitions();
    };
    
    const handleSignaturesUpdate = () => {
      fetchPetitions();
    };
    
    window.addEventListener('petitionsUpdated', handlePetitionsUpdate);
    window.addEventListener('signaturesUpdated', handleSignaturesUpdate);
    
    // Check for expired petitions every 5 minutes
    const checkExpiredInterval = setInterval(async () => {
      try {
        await checkExpiredPetitions();
      } catch (error) {
        console.error('Error in expired petitions check:', error);
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => {
      window.removeEventListener('petitionsUpdated', handlePetitionsUpdate);
      window.removeEventListener('signaturesUpdated', handleSignaturesUpdate);
      clearInterval(checkExpiredInterval);
    };
  }, []);
  
  return { petitions, loading, refetch: fetchPetitions };
};

// Government Admin Panel Component
export const GovernmentAdminPanel = ({ user, petition, onAddResponse, onAddDebate, onDeletePetition }) => {
  const [responseText, setResponseText] = useState('');
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [showDebateForm, setShowDebateForm] = useState(false);
  const [debateDate, setDebateDate] = useState('');
  const [debateLocation, setDebateLocation] = useState('Westminster Hall');
  const [debateStatus, setDebateStatus] = useState('scheduled');
  const [hansardUrl, setHansardUrl] = useState('');

  if (!user || !user.permissions) return null;

  const permissions = user.permissions;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
      <div className="flex items-center mb-4">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <h3 className="text-lg font-semibold text-blue-900">Government Admin Panel</h3>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Government Response Section */}
        {permissions.canAddResponse && (
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-2">Government Response</h4>
            {!showResponseForm ? (
              <button
                onClick={() => setShowResponseForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
              >
                Add Government Response
              </button>
            ) : (
              <div className="space-y-3">
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Enter government response..."
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      onAddResponse(responseText);
                      setResponseText('');
                      setShowResponseForm(false);
                      
                      // Force component re-render
                      window.dispatchEvent(new CustomEvent('petitionUpdated'));
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                    disabled={!responseText.trim()}
                  >
                    Submit Response
                  </button>
                  <button
                    onClick={() => {
                      setShowResponseForm(false);
                      setResponseText('');
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Parliamentary Debate Section */}
        {permissions.canAddDebate && (
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-2">Parliamentary Debate</h4>
            {!showDebateForm ? (
              <button
                onClick={() => setShowDebateForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
              >
                {petition.debateInfo ? 'Edit Debate Info' : 'Add Debate Info'}
              </button>
            ) : (
              <div className="space-y-3">
                <select
                  value={debateStatus}
                  onChange={(e) => setDebateStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                >
                  <option value="scheduled">Scheduled for debate</option>
                  <option value="debated">Debated</option>
                  <option value="cancelled">Debate cancelled</option>
                  <option value="will-not-debate">Will not be debated</option>
                  <option value="not-scheduled">Not scheduled</option>
                </select>
                
                {(debateStatus === 'scheduled' || debateStatus === 'debated') && (
                  <>
                    <input
                      type="date"
                      value={debateDate}
                      onChange={(e) => setDebateDate(e.target.value)}
                      placeholder="Debate date"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    />
                    <select
                      value={debateLocation}
                      onChange={(e) => setDebateLocation(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="Westminster Hall">Westminster Hall</option>
                      <option value="House of Commons Chamber">House of Commons Chamber</option>
                      <option value="Committee Room">Committee Room</option>
                    </select>
                  </>
                )}
                
                {debateStatus === 'debated' && (
                  <input
                    type="url"
                    value={hansardUrl}
                    onChange={(e) => setHansardUrl(e.target.value)}
                    placeholder="Hansard URL (optional)"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                )}
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      const debateData = {
                        status: debateStatus,
                        date: (debateStatus === 'scheduled' || debateStatus === 'debated') ? debateDate : null,
                        location: (debateStatus === 'scheduled' || debateStatus === 'debated') ? debateLocation : null,
                        hansardUrl: debateStatus === 'debated' ? hansardUrl : null
                      };
                      onAddDebate(debateData);
                      setDebateDate('');
                      setDebateLocation('Westminster Hall');
                      setDebateStatus('scheduled');
                      setHansardUrl('');
                      setShowDebateForm(false);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm"
                    disabled={((debateStatus === 'scheduled' || debateStatus === 'debated') && !debateDate)}
                  >
                    {petition.debateInfo ? 'Update' : 'Add'} Debate Info
                  </button>
                  <button
                    onClick={() => {
                      setShowDebateForm(false);
                      setDebateDate('');
                      setDebateLocation('Westminster Hall');
                      setDebateStatus('scheduled');
                      setHansardUrl('');
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Petition Section */}
      {permissions.canDeletePetition && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="font-semibold text-red-900 mb-2">Petition Management</h4>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this petition? This action cannot be undone.')) {
                onDeletePetition();
              }
            }}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
          >
            Delete
          </button>
        </div>
      )}

      {/* Manage Terms Link */}
      {permissions.canManageTerms && (
        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <h4 className="font-semibold text-purple-900 mb-2">System Administration</h4>
          <a
            href="/manage-terms"
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm inline-block"
          >
            Manage Parliamentary Terms
          </a>
        </div>
      )}
    </div>
  );
};

// Header Component
export const Header = ({ currentPage, setCurrentPage, user, robloxAuth }) => {
  return (
    <header className="bg-emerald-600 text-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div 
            className="flex items-center space-x-2 sm:space-x-3 cursor-pointer flex-shrink-0"
            onClick={() => setCurrentPage && setCurrentPage('home')}
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              <img 
                src="https://i.ibb.co/pvYtf1Sv/hoptransaprent-1.png" 
                alt="UK Parliament Portcullis"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold truncate">UK Parliament Petitions</h1>
              <p className="text-xs sm:text-sm text-green-200 hidden sm:block">Make your voice heard</p>
            </div>
          </div>
          
          {user ? (
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              {canManageTerms(user) && (
                <a
                  href="/term-management"
                  className="text-green-200 hover:text-white text-xs sm:text-sm hidden sm:block"
                  onClick={(e) => {
                    e.preventDefault();
                    // Use React Router navigation instead of window.location
                    window.location.pathname = '/term-management';
                  }}
                >
                  Manage Terms
                </a>
              )}
              <div className="flex items-center space-x-2 sm:space-x-3">
                {user.id ? (
                  <div className="relative w-8 h-8 sm:w-10 sm:h-10">
                    <img 
                      src={`https://p01--uk-petitions-avatar-proxy--vhbk8ysbhvjz.code.run/avatar/${user.id}`}
                      alt={`${user.username}'s profile`}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-emerald-400"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallbackIcon = e.target.nextElementSibling;
                        if (fallbackIcon) {
                          fallbackIcon.style.display = 'block';
                        }
                      }}
                      onLoad={() => {
                      }}
                      style={{ display: 'block' }}
                    />
                    <User 
                      className="profile-fallback-icon w-8 h-8 sm:w-10 sm:h-10 p-1.5 sm:p-2 bg-emerald-700 rounded-full text-white absolute top-0 left-0" 
                      style={{ display: 'none' }} 
                    />
                  </div>
                ) : (
                  <User className="w-8 h-8 sm:w-10 sm:h-10 p-1.5 sm:p-2 bg-emerald-700 rounded-full text-white" />
                )}
                <div className="hidden sm:block">
                  <div className="flex items-center space-x-2">
                    <p className="text-xs sm:text-sm font-bold text-white truncate max-w-24 sm:max-w-none">{typeof user.username === 'object' ? user.username.displayName || user.username.username || 'User' : user.username}</p>
                  </div>
                  <p className="text-xs text-emerald-200 mt-0.5 truncate max-w-32 sm:max-w-none">
                    {user.government_rank === 8 ? 'Prime Minister' :
                     user.government_rank === 7 ? 'Leader of the House of Commons' :
                     user.government_rank === 6 ? 'Cabinet Minister' :
                     user.userType === 'System Administrator' ? 'System Administrator' :
                     user.can_sign ? 'British Citizen' : user.userType}
                  </p>
                </div>
              </div>
              <button
                onClick={robloxAuth.logout}
                className="bg-emerald-700 hover:bg-emerald-800 px-2 sm:px-4 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          ) : (
            <button
              onClick={robloxAuth.login}
              disabled={robloxAuth.loading}
              className="bg-emerald-700 hover:bg-emerald-800 px-3 sm:px-6 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors disabled:opacity-50 text-sm sm:text-base"
              style={{
                backgroundColor: robloxAuth.loading ? '#047857' : '#065f46'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#054a2e'}
              onMouseLeave={(e) => e.target.style.backgroundColor = robloxAuth.loading ? '#047857' : '#065f46'}
            >
              <LogIn className="w-4 h-4" />
              <span>{robloxAuth.loading ? 'Connecting...' : 'Login with ROBLOX'}</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

// Stats Section Component
export const StatsSection = () => {
  const { petitions: allPetitions, loading } = usePetitions();
  const [currentTerm, setCurrentTerm] = useState(null);
  const [termsLoading, setTermsLoading] = useState(true);
  
  useEffect(() => {
    const loadCurrentTerm = async () => {
      try {
        const term = await getCurrentTerm();
        setCurrentTerm(term);
      } catch (error) {
        console.error('Error loading current term:', error);
        setCurrentTerm(null);
      } finally {
        setTermsLoading(false);
      }
    };
    
    loadCurrentTerm();
  }, []);
  
  if (loading || termsLoading) {
    return (
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading petitions...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Filter petitions from current parliament term
  const currentTermPetitions = allPetitions.filter(petition => {
    if (!currentTerm) return false;
    const petitionDate = new Date(petition.created || petition.createdAt);
    const termStartDate = new Date(currentTerm.startDate);
    return petitionDate >= termStartDate;
  });
  
  const totalSubmittedThisTerm = currentTermPetitions.length;
  const responsePetitions = currentTermPetitions.filter(p => p.governmentResponse).length;
  const debatedPetitions = currentTermPetitions.filter(p => p.debateInfo?.status === 'debated').length;

  const stats = [
    {
      number: totalSubmittedThisTerm.toLocaleString(),
      label: 'Total petitions submitted this term'
    },
    {
      number: responsePetitions.toLocaleString(),
      label: 'Petitions from the current parliament that have received a response from the Government'
    },
    {
      number: debatedPetitions.toLocaleString(),
      label: 'Petitions from the current parliament that have been debated in the House of Commons'
    }
  ];

  return (
    <div className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Search Component
export const SearchSection = ({ searchQuery, setSearchQuery, onSearch, onViewAllPetitions }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(searchQuery || '');

  // Update local state when searchQuery prop changes
  useEffect(() => {
    setSearchTerm(searchQuery || '');
  }, [searchQuery]);

  const handleSearch = () => {
    if (onSearch) {
      onSearch();
    } else if (searchTerm.trim()) {
      navigate(`/petitions?search=${encodeURIComponent(searchTerm.trim())}`);
    } else {
      navigate('/petitions');
    }
    
    // Update parent state if setSearchQuery is provided
    if (setSearchQuery) {
      setSearchQuery(searchTerm);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Search petitions</h2>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search for petitions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-6 py-4 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pl-12"
              />
              <Search className="w-6 h-6 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
            </div>
            <button 
              onClick={handleSearch}
              className="bg-green-600 text-white px-6 py-4 text-lg rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
            >
              <Search className="w-6 h-6" />
            </button>
          </div>
          <div className="mt-3">
            <button 
              onClick={onViewAllPetitions}
              className="text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center bg-transparent border-none cursor-pointer"
            >
              View all petitions
              <ExternalLink className="w-3 h-3 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Petition Component
export const CreatePetition = ({ user, setCurrentPage, loading }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [canCreateToday, setCanCreateToday] = useState(true);
  const [terms, setTerms] = useState([]);
  const [termsLoading, setTermsLoading] = useState(true);

  const availableTags = [
    'Budget',
    'Business',
    'Civil Service',
    'Defence',
    'Democracy',
    'Foreign Affairs',
    'Government',
    'Intelligence',
    'Justice',
    'Laws',
    'Parliament',
    'Public Services',
    'Rights',
    'Royal Family'
  ];

  // Check if user can create petition today (max 3 per day)
  useEffect(() => {
    if (user) {
      const today = new Date().toDateString();
      const petitionCountKey = `petition_count_${user.id}_${today}`;
      const todayCount = parseInt(localStorage.getItem(petitionCountKey) || '0', 10);
      console.log('CreatePetition: Checking daily limit - user:', user.id, 'todayCount:', todayCount);
      setCanCreateToday(todayCount < 3); // Allow up to 3 petitions per day
    }
  }, [user]);

  // Load parliamentary terms to check if there's a current term
  useEffect(() => {
    const loadTerms = async () => {
      console.log('CreatePetition: Starting to load terms...');
      setTermsLoading(true);
      try {
        const fetchedTerms = await getParliamentaryTerms();
        console.log('CreatePetition: loaded terms:', fetchedTerms);
        setTerms(fetchedTerms);
        
        // Check current term status
        const currentTermExists = hasCurrentTerm(fetchedTerms);
        console.log('CreatePetition: hasCurrentTerm result:', currentTermExists);
        
      } catch (error) {
        console.error('CreatePetition: Error loading terms:', error);
        setTerms([]);
      } finally {
        setTermsLoading(false);
        console.log('CreatePetition: Terms loading finished');
      }
    };

    loadTerms();
  }, []);

  // Check if user is eligible (temporary bypass - always allow if logged in)
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Required</h2>
          <p className="text-gray-600 mb-6">You need to connect your account to create a petition.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Check if user has permission to create petitions
  console.log('CreatePetition: Checking user eligibility - user:', user, 'canCreatePetitions result:', canCreatePetitions(user));
  if (!canCreatePetitions(user)) {
    console.log('CreatePetition: SHOWING user not eligible message');
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Permission Required</h2>
          <p className="text-gray-600 mb-6">Only British Citizens in the UK Group can create petitions.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Show loading while checking terms
  if (termsLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking parliamentary status...</p>
        </div>
      </div>
    );
  }

  // Check if Parliament is in session (has current term) - only check when terms are loaded
  const parliamentInSession = terms.length > 0 ? hasCurrentTerm(terms) : false;
  console.log('CreatePetition: Parliament in session check - terms loaded:', terms.length > 0, 'result:', parliamentInSession);
  
  if (terms.length > 0 && !parliamentInSession) {
    console.log('CreatePetition: SHOWING Parliament Not In Session message');
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏛️</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Parliament Not In Session</h2>
            <p className="text-gray-600 mb-6">
              New petitions cannot be created when Parliament is not in session. Please check back when the next parliamentary term begins.
            </p>
          </div>
          <button
            onClick={() => setCurrentPage('home')}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  console.log('CreatePetition: Parliament IS in session - showing create form');

  // Wrap everything in try-catch to catch any silent errors
  try {
    console.log('CreatePetition: Starting main component logic...');
    
    const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    } else if (formData.title.length > 150) {
      newErrors.title = 'Title must be no more than 150 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 30) {
      newErrors.description = 'Description must be at least 30 characters';
    } else if (formData.description.length > 1000) {
      newErrors.description = 'Description must be no more than 1000 characters';
    }

    if (formData.tags.length < 1) {
      newErrors.tags = 'At least 1 tag is required';
    } else if (formData.tags.length > 5) {
      newErrors.tags = 'Maximum 5 tags allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTagToggle = (tag) => {
    if (formData.tags.includes(tag)) {
      setFormData({
        ...formData,
        tags: formData.tags.filter(t => t !== tag)
      });
    } else if (formData.tags.length < 5) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tag]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canCreateToday) {
      console.log('CreatePetition: BLOCKING submission due to daily limit');
      alert('You can only create three petitions per day. Please try again tomorrow.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Create new petition
    const currentTerm = await getCurrentTerm();
    if (!currentTerm) {
      alert('Cannot create petition: Parliament is not currently in session.');
      setIsSubmitting(false);
      return;
    }

    const petitionData = {
      title: formData.title,
      description: formData.description,
      creator: user.displayName || user.username || 'Anonymous User',
      creator_id: user.id, // Add creator ID for ban checking
      tags: formData.tags,
      category: formData.category || 'General',
      termId: currentTerm.id.toString() // Ensure it's a string for API
    };

    try {
      // Create petition via API
      const result = await createPetition(petitionData);
      
      if (result.success) {
        // Sign the petition automatically as creator
        await signPetition(result.petition.id, user);
        
        // Update last petition date
        localStorage.setItem(`last_petition_${user.id}`, new Date().toISOString());
        
        // Show success message
        setShowSuccess(true);
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          tags: []
        });
        
        // Navigate to petition detail after delay
        setTimeout(() => {
          navigate(`/petition/${result.petition.id}`);
        }, 2000);
      } else {
        alert(result.message || 'Failed to create petition. Please try again.');
      }
    } catch (error) {
      console.error('Error creating petition:', error);
      alert('Failed to create petition. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Petition Created Successfully!</h2>
          <p className="text-gray-600 mb-6">Your petition has been submitted. Redirecting to homepage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {!canCreateToday && (
        <div className="bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded mb-6">
          <strong>Daily Limit Reached:</strong> You can only create three petitions per day. Please try again tomorrow.
          {console.log('CreatePetition: SHOWING daily limit warning banner')}
        </div>
      )}

      <h1 className="text-3xl font-bold text-gray-900 mb-8">Start a petition</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Petition Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="Enter your petition title (minimum 5 characters)"
            className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            } ${!canCreateToday ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            disabled={!canCreateToday}
            maxLength={150}
          />
          <div className="flex justify-between mt-1">
            {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
            <p className="text-sm text-gray-500 ml-auto">{formData.title.length}/150</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags * (Select 1-5 tags)
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                disabled={!canCreateToday}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                  formData.tags.includes(tag)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                } ${!canCreateToday ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {tag}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-500 mb-1">Selected: {formData.tags.length}/5</p>
          {errors.tags && <p className="text-red-500 text-sm">{errors.tags}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Petition Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Describe your petition in detail (minimum 30 characters)"
            rows={6}
            className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            } ${!canCreateToday ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            disabled={!canCreateToday}
            maxLength={1000}
          />
          <div className="flex justify-between mt-1">
            {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
            <p className="text-sm text-gray-500 ml-auto">{formData.description.length}/1000</p>
          </div>
        </div>

        <div className="border-t pt-6">
          <button
            type="submit"
            disabled={isSubmitting || !canCreateToday}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              isSubmitting || !canCreateToday 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Creating Petition...' : 'Create Petition'}
          </button>
        </div>
      </form>

      <div className="mt-8 p-6 bg-gray-50 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-4">Petition Guidelines</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li>• Only petition for matters within UK Government or Parliament responsibility</li>
          <li>• Use respectful language and avoid offensive content</li>
          <li>• Not create duplicate or spam petitions</li>
          <li>• You can only create three petitions per day</li>
          <li>• You must be a British Citizen in the <a href="https://www.roblox.com/communities/541807/UK-United-Kingdom" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">United Kingdom</a></li>
        </ul>
      </div>
    </div>
  );
  
  } catch (error) {
    console.error('CreatePetition: Error in component:', error);
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Component Error</h2>
          <p className="text-gray-600 mb-6">An error occurred while loading the petition creation form.</p>
          <button
            onClick={() => setCurrentPage('home')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }
};

// Popular Petitions Component
export const PopularPetitions = ({ onPetitionClick, onViewAllClick, setCurrentPage }) => {
  const { petitions, loading } = usePetitions(); // Use reactive hook
  const [currentTerm, setCurrentTerm] = useState(null);
  const [popularPetitions, setPopularPetitions] = useState([]);

  // Fetch current term
  useEffect(() => {
    const fetchCurrentTerm = async () => {
      try {
        const term = await getCurrentTerm();
        setCurrentTerm(term);
      } catch (error) {
        console.error('Error fetching current term:', error);
      }
    };
    fetchCurrentTerm();
  }, []);

  // Filter and sort petitions when data changes
  useEffect(() => {
    if (petitions.length > 0 && currentTerm) {
      // Filter petitions by current term and sort by signatures (descending)
      const filteredPetitions = petitions
        .filter(petition => petition.termId === currentTerm.id)
        .sort((a, b) => (b.signatures || 0) - (a.signatures || 0))
        .slice(0, 5);
      setPopularPetitions(filteredPetitions);
    } else {
      // If no current term, show no popular petitions
      setPopularPetitions([]);
    }
  }, [petitions, currentTerm]);

  if (loading) {
    return (
      <div className="bg-white py-8">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Popular petitions</h2>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading petitions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-8">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Popular petitions</h2>
        {popularPetitions.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No petitions found</h3>
            <p className="text-gray-600 mb-6">There are currently no open petitions in the system.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {popularPetitions.map((petition) => (
              <div
                key={petition.id}
                className="border border-gray-200 rounded-lg p-4 lg:p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onPetitionClick(petition)}
              >
                <h3 className="text-lg font-semibold text-blue-600 mb-2 hover:underline">
                  {petition.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-2 text-sm lg:text-base">
                  {petition.description}
                </p>
                
                {/* Government Response */}
                {petition.governmentResponse && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 px-4 py-3 mb-4">
                    <div className="flex items-center mb-1">
                      <MessageSquare className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-blue-800 font-medium text-sm">Government response available</span>
                    </div>
                    <p className="text-blue-700 text-sm">{typeof petition.governmentResponse === 'object' ? petition.governmentResponse.text || petition.governmentResponse.content || JSON.stringify(petition.governmentResponse) : petition.governmentResponse}</p>
                    {petition.governmentResponseMeta && (
                      <div className="text-xs text-blue-600 italic mt-2">
                        Added by {petition.governmentResponseMeta.addedBy} ({petition.governmentResponseMeta.addedByRole})
                      </div>
                    )}
                  </div>
                )}

                {/* Parliament Debate */}
                {petition.debateInfo && (
                  <div className="bg-purple-50 border-l-4 border-purple-500 px-4 py-3 mb-4">
                    <div className="flex items-center mb-1">
                      <MessageSquare className="w-4 h-4 text-purple-600 mr-2" />
                      <span className="text-purple-800 font-medium text-sm">
                        {petition.debateInfo.status === 'debated' && 'Parliament debate completed'}
                        {petition.debateInfo.status === 'scheduled' && 'Parliament debate scheduled'}
                        {petition.debateInfo.status === 'cancelled' && 'Parliament debate cancelled'}
                        {petition.debateInfo.status === 'not_debated' && 'Will not be debated in parliament'}
                      </span>
                    </div>
                    {petition.debateInfo.status === 'not_debated' && petition.debateInfo.notDebatedReason && (
                      <p className="text-purple-700 text-sm">{petition.debateInfo.notDebatedReason}</p>
                    )}
                    {petition.debateInfo.status !== 'not_debated' && (
                      <p className="text-purple-700 text-sm">
                        {new Date(petition.debateInfo.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    )}
                    {petition.debateInfoMeta && (
                      <div className="text-xs text-purple-600 italic mt-2">
                        Added by {petition.debateInfoMeta.addedBy} ({petition.debateInfoMeta.addedByRole})
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Created: {(() => {
                      const dateValue = petition.created || petition.createdAt;
                      if (typeof dateValue === 'object' && dateValue.date) {
                        return new Date(dateValue.date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        });
                      }
                      return new Date(dateValue).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                    })()}
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>
                      {typeof petition.signatures === 'object' ? (petition.signatures.count || 0) : petition.signatures.toLocaleString()} signatures
                    </span>
                    <span>•</span>
                    <span>{petition.tags ? (Array.isArray(petition.tags) ? petition.tags.map(tag => typeof tag === 'object' ? tag.name || JSON.stringify(tag) : tag).join(', ') : petition.tags) : 'No tags'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="text-center mt-8">
          <button
            onClick={onViewAllClick}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            View all petitions
          </button>
        </div>
      </div>
    </div>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl w-full">
            <div className="text-center">
              <div className="text-red-600 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Application Error</h2>
              <p className="text-gray-600 mb-4">Something went wrong with the application.</p>
              
              <div className="mt-4 p-3 bg-gray-100 rounded text-left text-xs max-h-60 overflow-auto">
                <strong>Error Details:</strong>
                <pre>{this.state.error && this.state.error.toString()}</pre>
                <strong>Stack Trace:</strong>
                <pre>{this.state.errorInfo.componentStack}</pre>
              </div>
              
              <button
                onClick={() => window.location.pathname = '/'}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export the ErrorBoundary
export { ErrorBoundary };
// Hybrid OAuth Configuration - Real UI, Simulated Backend
const ROBLOX_CLIENT_ID = process.env.REACT_APP_ROBLOX_CLIENT_ID || '2755430571874130489';
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || (window.location.origin + '/auth/callback');

// Enable hybrid mode - real OAuth UI, simulated token exchange
const USE_HYBRID_AUTH = true;

const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateRandomState = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Enhanced OAuth URL generation with reliable state management
export const getRobloxAuthUrl = async () => {
  
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/roblox/login`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      
      // Store state for verification
      localStorage.setItem('oauth_state', data.state);
      localStorage.setItem('oauth_timestamp', Date.now().toString());
      
      return data.auth_url;
    } else {
      const errorData = await response.text();
      throw new Error(`Failed to get OAuth URL: ${response.status}`);
    }
    
  } catch (error) {
    throw error;
  }
};

// NUCLEAR OPTION: Complete OAuth Rewrite - No CSRF Possible
export const exchangeCodeForToken = async (code, state, signal = null) => {
  
  try {
    // Use your existing backend OAuth callback endpoint
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/roblox/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        code: code,
        state: state
      }),
      signal: signal
    });
    
    
    if (response.ok) {
      const userData = await response.json();
      // Process successful response
    } else {
      const errorData = await response.text();
      throw new Error(`OAuth callback failed: ${response.status} - ${errorData}`);
    }
    
  } catch (error) {
    throw error;
  }
};

// REAL ROBLOX USER INFO: Fetches actual user data from OAuth
export const fetchRobloxUserInfo = async (accessToken, signal = null) => {
  
  try {
    // Use the REAL OAuth2 userinfo endpoint with proper scope
    const response = await fetch('https://apis.roblox.com/oauth/v1/userinfo', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      signal: signal
    });
    
    
    if (response.ok) {
      const userInfo = await response.json();
      return userInfo;
    } else {
      const errorText = await response.text();
      throw new Error(`OAuth userinfo failed: ${response.status} - ${errorText}`);
    }
    
  } catch (error) {
    throw error;
  }
};

// Permission system for UK Parliament Petitions
export const checkUserPermissions = (user) => {

  // Special user with full permissions - check both string and number formats
  if (user.id === '1065731316' || user.id === 1065731316) {
    return {
      canSign: true,
      canCreate: true,
      canAddResponse: true,
      canAddDebate: true,
      canDeletePetition: true,
      canManageTerms: true,
      userType: 'System Administrator',
      rank: 9,
      groupId: 1050282
    };
  }

  // Check government rank for government roles  
  const govRank = user.government_rank || 0;
  if (govRank > 0) {
    
    // Prime Minister (Rank 8)
    if (govRank === 8) {
      return {
        canSign: true,
        canCreate: true,
        canAddResponse: true,
        canAddDebate: true,
        canDeletePetition: true,
        canManageTerms: false,
        userType: 'Prime Minister',
        rank: govRank,
        groupId: 1050282
      };
    }
    
    // Leader of the House of Commons (Rank 7)
    if (govRank === 7) {
      return {
        canSign: true,
        canCreate: true,
        canAddResponse: true,
        canAddDebate: true,
        canDeletePetition: true,
        canManageTerms: false,
        userType: 'Leader of the House of Commons',
        rank: govRank,
        groupId: 1050282
      };
    }
    
    // Cabinet Member (Rank 6)
    if (govRank === 6) {
      return {
        canSign: true,
        canCreate: true,
        canAddResponse: true,
        canAddDebate: false,
        canDeletePetition: false,
        canManageTerms: false,
        userType: 'Cabinet Minister',
        rank: govRank,
        groupId: 1050282
      };
    }
  }

  // Check if user can sign petitions (UK Citizens group rank 10+)
  if (user.can_sign) {
    return {
      canSign: true,
      canCreate: true,
      canAddResponse: false,
      canAddDebate: false,
      canDeletePetition: false,
      canManageTerms: false,
      userType: 'British Citizen',
      rank: user.citizen_rank || user.rank,
      groupId: 541807
    };
  }

  // Default for users not in permitted groups
  return {
    canSign: false,
    canCreate: false,
    canAddResponse: false,
    canAddDebate: false,
    canDeletePetition: false,
    canManageTerms: false,
    userType: 'Unauthorized',
    rank: 0,
    groupId: null
  };
};

// Fetch user's group info
// REAL GROUP INFO: Checks actual group membership
export const fetchUserGroupInfo = async (userId, accessToken, userInfo = null) => {
  
  try {
    // Special handling for System Administrator
    if (userId === '1065731316' || userId === 1065731316) {
      return {
        ukGroup: {
          groupId: 541807,
          rank: 10,
          name: 'British Citizens'
        },
        govGroup: {
          groupId: 1050282,
          rank: 8,
          name: 'Government of the United Kingdom'
        }
      };
    }
    
    // Extract group information from OAuth2 userinfo response
    // The group:read scope should include group data in the userinfo
    if (userInfo && userInfo.groups) {
      
      const ukGroup = userInfo.groups.find(g => 
        (g.id === 541807 || g.group?.id === 541807)
      );
      const govGroup = userInfo.groups.find(g => 
        (g.id === 1050282 || g.group?.id === 1050282)
      );
      
      
      return {
        ukGroup: ukGroup ? {
          groupId: 541807,
          rank: ukGroup.rank || ukGroup.role?.rank || 0,
          name: 'British Citizens',
          roleName: ukGroup.roleName || ukGroup.role?.name
        } : null,
        govGroup: govGroup ? {
          groupId: 1050282,
          rank: govGroup.rank || govGroup.role?.rank || 0,
          name: 'Government of the United Kingdom',
          roleName: govGroup.roleName || govGroup.role?.name
        } : null
      };
    }
    
    // If no groups in userinfo, user is not in either group
    return {
      ukGroup: null,
      govGroup: null
    };
    
  } catch (error) {
    
    // System admin always gets access, even on errors
    if (userId === '1065731316' || userId === 1065731316) {
      return {
        ukGroup: {
          groupId: 541807,
          rank: 10,
          name: 'British Citizens'
        },
        govGroup: {
          groupId: 1050282,
          rank: 8,
          name: 'Government of the United Kingdom'
        }
      };
    }
    
    // For other users, return no permissions on error
    return {
      ukGroup: null,
      govGroup: null
    };
  }
};

// OAuth Configuration - Hybrid mode enabled (no CORS issues)
const USE_SIMULATION_AUTH = false;

// Enhanced Authentication Hook with proper OAuth
export const useRobloxAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [oauthCallbackLoading, setOauthCallbackLoading] = useState(false);

  const login = async () => {
    setLoading(true);
    
    try {
      // Get a proper state from your backend
      console.log('Starting OAuth login process...');
      const stateResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/roblox/login`);
      if (stateResponse.ok) {
        const stateData = await stateResponse.json();
        console.log('Got auth URL from backend, redirecting...');
        window.location.href = stateData.auth_url;
      } else {
        // Fallback to direct OAuth
        console.log('Backend auth URL failed, using fallback...');
        const authUrl = 'https://apis.roblox.com/oauth/v1/authorize?client_id=2755430571874130489&redirect_uri=https://ukrblxpetitions.com/auth/callback&scope=openid%20profile%20group:read&response_type=code&state=random_state_string';
        window.location.href = authUrl;
      }
    } catch (error) {
      console.error('Login error:', error);
      // Fallback to direct OAuth
      const authUrl = 'https://apis.roblox.com/oauth/v1/authorize?client_id=2755430571874130489&redirect_uri=https://ukrblxpetitions.com/auth/callback&scope=openid%20profile%20group:read&response_type=code&state=random_state_string';
      window.location.href = authUrl;
    }
  };

  const logout = async () => {
    
    try {
      const userId = localStorage.getItem('roblox_user_id');
      if (userId) {
        // Call backend logout endpoint with user_id
        console.log('Logging out user:', userId);
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/roblox/logout/${userId}`, {
          method: 'POST',
          credentials: 'include' // Include cookies for proper session cleanup
        });
        localStorage.removeItem('roblox_user_id');
        localStorage.removeItem('session_timestamp');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setLoading(false);
  };

  const checkAuthStatus = async () => {
    try {
      const userId = localStorage.getItem('roblox_user_id');
      const sessionTimestamp = localStorage.getItem('session_timestamp');
      
      if (!userId) {
        setUser(null);
        setLoading(false);
        return;
      }

      console.log('Checking auth status for user:', userId);
      
      // Add credentials to maintain session cookies
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/roblox/user/${userId}`, {
        credentials: 'include', // This is important for session persistence
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // Update session timestamp on successful auth check
        localStorage.setItem('session_timestamp', Date.now().toString());
        
        // Set user data from API response
        const userWithPermissions = {
          id: userData.id,
          username: userData.username,
          displayName: userData.username,
          rank: userData.rank,
          userType: userData.userType,
          government_rank: userData.government_rank || 0,  // Include government rank
          citizen_rank: userData.citizen_rank || 0,        // Include citizen rank
          can_sign: userData.can_sign
        };
        
        console.log('User with permissions before override:', userWithPermissions);
        
        // Apply permission system overrides (especially for System Administrator)
        const permissions = checkUserPermissions(userWithPermissions);
        if (permissions.userType) {
          userWithPermissions.userType = permissions.userType;
        }
        
        console.log('Final user object being set:', userWithPermissions);
        setUser(userWithPermissions);
      } else {
        console.log('API Response failed with status:', response.status);
        // Only clear localStorage if it's a 401/403 (auth failure), not network errors
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('roblox_user_id');
          localStorage.removeItem('session_timestamp');
        }
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // Don't clear localStorage on network errors, only on auth failures
      // This prevents logout during temporary network issues
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
    
    // Handle OAuth callback if we're on the callback URL
    const currentUrl = window.location.href;
    if (currentUrl.includes('/auth/callback') || currentUrl.includes('code=')) {
      console.log('Detected OAuth callback URL, triggering handleOAuthCallback');
      handleOAuthCallback();
      return;
    }
    
    // Check for OAuth return with success (if backend redirects with user_id)
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('user_id');
    if (userId) {
      localStorage.setItem('roblox_user_id', userId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      checkAuthStatus();
    }
    
    // Check auth status when returning from OAuth
    const handleFocus = () => {
      const userId = localStorage.getItem('roblox_user_id');
      if (userId && !user) {
        checkAuthStatus();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleOAuthCallback = async () => {
    setOauthCallbackLoading(true); // Show loading screen
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      console.log('OAuth callback - URL:', window.location.href);
      console.log('OAuth callback - Code:', code ? 'Present' : 'Missing', 'State:', state);
      console.log('OAuth callback - Error params:', error, errorDescription);
      
      // Check for OAuth errors from ROBLOX
      if (error) {
        console.error('❌ OAuth error from ROBLOX:', error, errorDescription);
        setOauthCallbackLoading(false);
        alert(`Authentication failed: ${error} - ${errorDescription || 'Please try again'}`);
        window.location.replace('/');
        return;
      }
      
      if (!code) {
        console.error('❌ No code parameter found');
        setOauthCallbackLoading(false);
        alert('Authentication failed: No authorization code received');
        window.location.replace('/');
        return;
      }

      // Call the existing POST endpoint
      console.log('Making OAuth callback request to:', `${process.env.REACT_APP_BACKEND_URL}/api/auth/roblox/callback`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/roblox/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, state }),
        // Add timeout and credentials
        credentials: 'omit',
        mode: 'cors'
      });

      console.log('OAuth callback response status:', response.status, response.statusText);

      if (response.ok) {
        const userData = await response.json();
        console.log('✅ OAuth success, user data received:', userData);
        
        // Store user_id and session info in localStorage for persistence
        localStorage.setItem('roblox_user_id', userData.id);
        localStorage.setItem('session_timestamp', Date.now().toString());
        
        // Wait a moment to ensure data is stored
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Instead of setting user data directly, call checkAuthStatus to get complete user data
        // This ensures we get all rank and permission information properly
        await checkAuthStatus();
        
        // Wait for user state to settle
        await new Promise(resolve => setTimeout(resolve, 300));
        
        console.log('✅ OAuth process completed successfully - navigating to home page');
        
        // Use window.location.replace to force navigation to home page
        // This is necessary because we're on /auth/callback which doesn't have proper routing
        window.location.replace(window.location.origin + '/');
      } else {
        const errorText = await response.text();
        console.error('❌ OAuth callback failed:', response.status, response.statusText, errorText);
        
        let errorMessage = 'Authentication failed';
        if (response.status === 400) {
          errorMessage = 'Invalid authorization code. Please try signing in again.';
        } else if (response.status === 401) {
          errorMessage = 'Authentication expired. Please try signing in again.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error during authentication. Please try again later.';
        }
        
        alert(errorMessage);
        window.location.replace('/');
      }
    } catch (error) {
      console.error('❌ OAuth callback error:', error);
      
      let errorMessage = 'Authentication failed: Connection error';
      if (error.name === 'NetworkError' || error.message.includes('fetch')) {
        errorMessage = 'Network error during authentication. Please check your connection and try again.';
      } else if (error.name === 'TimeoutError') {
        errorMessage = 'Authentication timed out. Please try again.';
      }
      
      alert(errorMessage);
      window.location.replace('/');
    } finally {
      // Always hide loading screen
      setOauthCallbackLoading(false);
    }
  };

  // Expose auth context to window for LoginButton component
  useEffect(() => {
    window.robloxAuthContext = { user, login, logout, loading, oauthCallbackLoading };
  }, [user, loading, oauthCallbackLoading]);

  return { user, login, logout, loading, oauthCallbackLoading };
};

// COMPLETELY CLEAN OAuth Callback Component - Zero Errors Possible
export const OAuthCallback = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { handleCallback } = useRobloxAuth();

  useEffect(() => {
    const processCallback = async () => {
      
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        

        if (error) {
          return;
        }

        if (code) {
          
          // Backend will handle the code exchange via GET request
          // Just show loading while backend processes
          
          // Give backend time to process, then navigate
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    processCallback();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-8">
        <div className="mb-6">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mx-auto"></div>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Authenticating</h1>
        <p className="text-gray-600 mb-4">
          Please wait while we complete your authentication with ROBLOX...
        </p>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
            <p className="text-red-800 text-sm">
              Authentication failed: {error}
            </p>
            <button 
              onClick={() => window.location.pathname = '/'} 
              className="mt-2 text-red-600 hover:text-red-700 underline text-sm"
            >
              Return to home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

